import { makeAutoObservable, runInAction } from 'mobx';
import { ExpenseCompsResponse, FetchExpenseCompsParams } from './types';
import { fetchExpenseComps } from './api';
import { ExpenseComp } from 'types/underwriting/income-approach/expenses';

export class ExpenseCompsStore {
  data: ExpenseComp[] = [];
  loading = false;
  totalCount = 0;
  availableLocations: string[] = [];
  availableStates: string[] = [];
  selectedExpenseComps: ExpenseComp[] = [];
  error: string | undefined = undefined;

  constructor() {
    makeAutoObservable(this);
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  setError(error: string | undefined) {
    this.error = error;
  }

  setData(response: ExpenseCompsResponse) {
    runInAction(() => {
      this.data = response.expenseComps;
      this.totalCount = response.totalCount;
      this.availableLocations = response.availableLocations;
      this.availableStates = response.availableStates;
    });
  }

  selectExpenseComp(expenseComp: ExpenseComp) {
    const exists = this.selectedExpenseComps.find((comp) => comp._id === expenseComp._id);
    if (!exists) {
      this.selectedExpenseComps.push(expenseComp);
    }
  }

  deselectExpenseComp(id: string) {
    this.selectedExpenseComps = this.selectedExpenseComps.filter((comp) => comp._id !== id);
  }

  clearSelectedComps() {
    this.selectedExpenseComps = [];
  }

  clearData() {
    runInAction(() => {
      this.data = [];
      this.totalCount = 0;
      this.availableLocations = [];
      this.availableStates = [];
      this.error = undefined;
    });
  }

  async fetchExpenseComps(params: FetchExpenseCompsParams): Promise<ExpenseCompsResponse> {
    this.setLoading(true);
    this.setError(undefined);

    try {
      const response = await fetchExpenseComps(params);
      this.setData(response);
      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch expense comparables';
      this.setError(errorMessage);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }
}
